<template>
  <DialogRoot v-model:open="showPreferences">
    <DialogTrigger v-bind="$attrs">
      <slot>Cookie Preferences</slot>
    </DialogTrigger>

    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 bg-black/60 z-[100] grid place-items-center overflow-y-auto py-6 px-4"
      >
        <DialogContent
          class="bg-beige border-2 border-black rounded-xl shadow-[0_8px_0_0_rgba(0,0,0,0.2)] max-w-4xl"
        >
          <div class="p-4 space-y-4">
            <!-- Header -->
            <div class="flex items-center justify-between">
              <DialogTitle class="text-2xl font-bold text-black font-Kontesa">
                Cookie Preferences
              </DialogTitle>
              <DialogClose as-child>
                <button
                  class="size-8 p-1 text-black/60 hover:text-black rounded-lg hover:bg-black/10 transition-colors"
                  aria-label="Close"
                >
                  <Icon name="lucide:x" class="size-full" />
                </button>
              </DialogClose>
            </div>

            <!-- Current Status -->
            <div
              v-if="consentSummary"
              class="bg-white/50 border-2 border-black/10 rounded-lg p-4 text-sm text-black/70"
            >
              <p>
                <strong class="text-black">Last updated:</strong>
                {{ consentSummary.timestamp }}
              </p>
              <p class="mt-1">
                <strong class="text-black">Accepted:</strong>
                {{
                  consentSummary.acceptedCategories.length > 0
                    ? consentSummary.acceptedCategories.join(", ")
                    : "Essential cookies only"
                }}
              </p>
            </div>

            <!-- Description -->
            <DialogDescription class="text-sm text-black/80 leading-relaxed">
              Customize your cookie preferences below. Note that disabling
              certain cookies may limit site functionality and your overall
              experience.
            </DialogDescription>

            <!-- Recommendation Notice -->
            <div
              class="bg-[#017735]/10 border border-[#017735]/20 rounded-lg p-4"
            >
              <div class="flex items-start gap-3">
                <Icon
                  name="lucide:lightbulb"
                  class="w-5 h-5 text-[#017735] mt-0.5 flex-shrink-0"
                />
                <div>
                  <h4 class="font-semibold text-[#017735] font-AeonikPro">
                    Recommended setting
                  </h4>
                  <p class="text-sm text-[#017735]/80 mt-1">
                    For the best experience, we recommend accepting all cookies.
                    This helps us provide personalized content and improve our
                    services.
                  </p>
                </div>
              </div>
            </div>

            <!-- Cookie Categories -->
            <div class="space-y-4">
              <div
                v-for="category in cookieCategories"
                :key="category.id"
                class="border-2 border-black/10 rounded-lg p-5 bg-white/50"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1 space-y-3">
                    <div class="flex items-center gap-3">
                      <h3 class="font-bold text-black font-AeonikPro">
                        {{ category.name }}
                        <span
                          v-if="category.required"
                          class="ml-1 px-2 py-1 text-xs font-medium text-white bg-black rounded-full"
                        >
                          Required
                        </span>
                        <span
                          v-else-if="
                            category.id === 'analytics' ||
                            category.id === 'marketing'
                          "
                          class="ml-1 px-2 py-1 text-xs font-medium text-[#017735] bg-[#017735]/10 rounded-full"
                        >
                          Recommended
                        </span>
                      </h3>
                    </div>
                    <p class="text-sm text-black/70 leading-relaxed">
                      {{ category.description }}
                    </p>
                    <div
                      v-if="
                        !category.required &&
                        (category.id === 'analytics' ||
                          category.id === 'marketing')
                      "
                      class="text-xs text-[#017735]/80"
                    >
                      💡 Enabling this helps us improve your experience
                    </div>
                  </div>

                  <!-- Toggle Switch -->
                  <div class="ml-6">
                    <SwitchRoot
                      v-model:checked="category.enabled"
                      :disabled="category.required"
                      class="w-12 h-7 bg-black/20 rounded-full relative data-[state=checked]:bg-[#017735] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SwitchThumb
                        class="block w-6 h-6 bg-white rounded-full shadow-lg transform transition-transform data-[state=checked]:translate-x-5 translate-x-0.5"
                      />
                    </SwitchRoot>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-col gap-4 pt-6 border-t-2 border-black/10">
              <!-- Primary CTA -->
              <button
                @click="acceptAll"
                class="w-full px-8 py-4 text-lg font-bold text-white bg-[#017735] rounded-xl hover:bg-[#015a29] transition-all duration-200 shadow-[0_4px_0_0_#015a29] hover:shadow-[0_2px_0_0_#015a29] hover:translate-y-[2px] font-Kontesa uppercase tracking-wide"
              >
                ✨ Accept All
              </button>

              <!-- Secondary options -->
              <div class="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  @click="savePreferences"
                  class="px-6 py-3 text-sm font-medium text-black bg-white/50 border-2 border-black/20 rounded-lg hover:bg-black/5 transition-colors font-AeonikPro"
                >
                  Save custom preferences
                </button>
                <button
                  @click="acceptEssential"
                  class="px-4 py-2 text-xs font-medium text-black/60 hover:text-black transition-colors underline underline-offset-2"
                >
                  Essential only (limited functionality)
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogOverlay>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
});
const {
  cookieCategories,
  acceptAll,
  acceptEssential,
  savePreferences,
  getConsentSummary,
  showPreferences,
} = useCookieConsent();

const consentSummary = computed(() => getConsentSummary());
</script>
